import React, { useEffect, useState } from "react";
import { collection, query, getDocs, doc, deleteDoc } from "firebase/firestore";
import { auth, db, storage } from "../firebase";
import { TextField, Typography, Box, Modal, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";
import ViewInvoiceModal from "./modals/ViewInvoiceModal";
import EditInvoiceModal from "./modals/EditInvoiceModal";
import InvoiceUpload from "./InvoiceUpload";
import InvoiceActions from "./InvoiceActions";
import InvoiceTable from "./InvoiceTable";
import FilterControls from "./FilterControls";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
} from "firebase/storage";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { processFile } from "../utils/extraction"; // Import the new processFile function

export interface Invoice {
  id: string;
  name: string;
  fileUrl: string;
  fileName: string;
  date: string;
  invoiceNumber: number;
  category?: string;
  subtotal: number;
  vatTotal: number;
  total: number;
  currency?: string;
  clientName?: string; // Optional client name
  dateScanned?: string; // Optional date scanned
  invoiceData?: string; // Full invoice snapshot (JSON) used to prefill the generator when editing
}

interface InvoicesProps {
  type: "purchase" | "sales" | "expense";
}

const Invoices: React.FC<InvoicesProps> = ({ type }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [modalType, setModalType] = useState<"view" | "edit" | "upload">(
    "view",
  );
  const [filter, setFilter] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Invoice;
    direction: "ascending" | "descending";
  } | null>(null);

  const [yearFilter, setYearFilter] = useState<string>("");
  const [monthFilter, setMonthFilter] = useState<string>("");
  const [quarterFilter, setQuarterFilter] = useState<string>("");

  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);

  const fetchInvoices = async () => {
    if (modalIsOpen && modalType === "upload") {
      closeModal();
    }
    const user = auth.currentUser;
    if (!user) {
      return;
    }
    const userId = user.uid;
    const collectionName =
      type === "purchase"
        ? "purchaseInvoices"
        : type === "sales"
          ? "salesInvoices"
          : "expenseInvoices";
    const q = query(collection(db, collectionName, userId, "userInvoices"));
    const querySnapshot = await getDocs(q);
    setInvoices(
      querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            subtotal: 0,
            vatTotal: 0,
            total: 0,
            ...doc.data(),
          }) as Invoice, // Default subtotal, vatTotal, and total to 0
      ),
    );
    console.log(invoices);
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const openModal = (
    type: "view" | "edit" | "upload",
    invoice: Invoice | null = null,
  ) => {
    setSelectedInvoice(invoice);
    setModalType(type);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedInvoice(null);
  };

  const handleDelete = async () => {
    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    const user = auth.currentUser;
    if (!user) return;
    const userId = user.uid;

    const collectionName =
      type === "purchase"
        ? "purchaseInvoices"
        : type === "sales"
          ? "salesInvoices"
          : "expenseInvoices";

    const storage = getStorage();

    for (const invoiceId of selectedInvoices) {
      const invoice = invoices.find((inv) => inv.id === invoiceId);

      if (invoice) {
        try {
          const fileRef = ref(
            storage,
            `${collectionName}/${userId}/${invoice.fileName}`,
          );
          await deleteObject(fileRef);
        } catch (error) {
          console.error("Error deleting file from Firebase Storage:", error);
        }

        // Delete the document from Firestore
        try {
          const invoiceDoc = doc(
            db,
            collectionName,
            userId,
            "userInvoices",
            invoice.id,
          );
          await deleteDoc(invoiceDoc);

          // Update local state
          setInvoices((prevInvoices) =>
            prevInvoices.filter((inv) => inv.id !== invoice.id),
          );
        } catch (error) {
          console.error(
            "Error deleting invoice document from Firestore:",
            error,
          );
        }
      }
    }

    Swal.fire("Deleted!", "Selected invoices have been deleted.", "success");
    setSelectedInvoices([]);
  };

  const downloadInvoice = async (invoice: Invoice) => {
    const link = document.createElement("a");
    link.href = invoice.fileUrl;
    link.download = getFileNameWithExtension(invoice.fileName);
    console.log(link.download);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const getFileNameWithExtension = (fileName: string) => {
    console.log(fileName);

    if (!fileName.includes(".")) {
      return `${fileName}.png`;
    }
    return fileName;
  };
  const downloadAllInvoices = async () => {
    const zip = new JSZip();
    const user = auth.currentUser;
    if (!user) return;

    const userId = user.uid;
    const collectionName =
      type === "purchase"
        ? "purchaseInvoices"
        : type === "sales"
          ? "salesInvoices"
          : "expenseInvoices";

    for (const invoiceId of selectedInvoices) {
      const invoice = invoices.find((inv) => inv.id === invoiceId);
      if (invoice) {
        try {
          const fileRef = ref(
            storage,
            `${collectionName}/${userId}/${invoice.fileName}`,
          );

          const fileUrl = await getDownloadURL(fileRef);
          const response = await fetch(fileUrl);
          const blob = await response.blob();
          zip.file(getFileNameWithExtension(invoice.fileName), blob);
        } catch (error) {
          console.error(`Error fetching file ${invoice.fileName}:`, error);
          continue;
        }
      }
    }

    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, `${type}_invoices.zip`);
    });
  };

  const handleRescan = async () => {
    const user = auth.currentUser;
    if (!user) {
      Swal.fire("Error", "No user logged in", "error");
      return;
    }

    Swal.fire({
      title: "Rescanning invoices...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      for (const invoiceId of selectedInvoices) {
        const invoice = invoices.find((inv) => inv.id === invoiceId);
        if (invoice) {
          const response = await fetch(invoice.fileUrl);
          const blob = await response.blob();
          const file = new File([blob], invoice.fileName);

          await processFile(file, user, db, type, true, invoice.id);
        }
      }
      Swal.fire(
        "Success",
        "Selected invoices rescanned successfully",
        "success",
      );
      fetchInvoices();
    } catch (error) {
      console.error("Error during rescan process:", error);
      Swal.fire(
        "Error",
        "An error occurred during the rescan process",
        "error",
      );
    } finally {
      setSelectedInvoices([]);
    }
  };

  const exportToCSV = () => {
    const toExport = invoices.filter((inv) =>
      selectedInvoices.includes(inv.id),
    );
    if (toExport.length === 0) return;

    const headers = [
      "Invoice Number",
      "Invoice Name",
      "Date",
      "Scan Date",
      type === "sales" ? "Client Name" : "Supplier Name",
      "Category",
      "Currency",
      "Subtotal",
      "VAT",
      "Total",
    ];

    const escape = (val: any) => {
      const str = val == null ? "" : String(val);
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const rows = toExport.map((inv) => [
      escape(inv.invoiceNumber),
      escape(inv.name),
      escape(inv.date),
      escape(inv.dateScanned ?? ""),
      escape(inv.clientName ?? ""),
      escape(inv.category ?? ""),
      escape(inv.currency ?? "EUR"),
      escape(inv.subtotal?.toFixed(2)),
      escape(inv.vatTotal?.toFixed(2)),
      escape(inv.total?.toFixed(2)),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, `${type}_invoices_export.csv`);
  };

  const handleSelectInvoice = (invoiceIds: string[]) => {
    setSelectedInvoices(invoiceIds);
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const [day, month, year] = invoice.date.split("/");
    const invoiceDate = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
    );

    const filterByYear = yearFilter
      ? invoiceDate.getFullYear().toString() === yearFilter
      : true;
    const filterByMonth = monthFilter
      ? (invoiceDate.getMonth() + 1).toString() === monthFilter
      : true;
    const filterByQuarter = quarterFilter
      ? Math.ceil((invoiceDate.getMonth() + 1) / 3).toString() === quarterFilter
      : true;

    const filterText = filter.toLowerCase();
    const matchesFilter =
      invoice.fileName.toLowerCase().includes(filterText) ||
      (invoice.clientName &&
        invoice.clientName.toLowerCase().includes(filterText)) ||
      invoice.date.toLowerCase().includes(filterText) ||
      invoice.total.toString().includes(filterText);

    return matchesFilter && filterByYear && filterByMonth && filterByQuarter;
  });

  const sortedInvoices = React.useMemo(() => {
    let sortableInvoices = [...filteredInvoices];

    if (sortConfig !== null) {
      sortableInvoices.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === undefined || bValue === undefined) {
          return 0;
        }

        // Check if the key is a date field and handle date comparison
        if (sortConfig.key === "date" || sortConfig.key === "dateScanned") {
          // Parse the dates and handle invalid date formats
          const dateA =
            typeof aValue === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(aValue)
              ? new Date(aValue.split("/").reverse().join("/"))
              : null;
          const dateB =
            typeof bValue === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(bValue)
              ? new Date(bValue.split("/").reverse().join("/"))
              : null;

          // Sort valid dates; move invalid dates to the bottom
          if (dateA && dateB) {
            return sortConfig.direction === "ascending"
              ? dateA.getTime() - dateB.getTime()
              : dateB.getTime() - dateA.getTime();
          } else if (dateA) {
            return sortConfig.direction === "ascending" ? -1 : 1; // Valid date A goes first
          } else if (dateB) {
            return sortConfig.direction === "ascending" ? 1 : -1; // Valid date B goes first
          } else {
            return 0; // Both are invalid, keep the original order
          }
        }

        // Fallback for non-date fields (general string/number comparison)
        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableInvoices;
  }, [filteredInvoices, sortConfig]);

  const requestSort = (key: keyof Invoice) => {
    let direction: "ascending" | "descending" = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: keyof Invoice) => {
    if (sortConfig && sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? "▲" : "▼";
    }
    return "";
  };

  const totalSubtotal = sortedInvoices.reduce(
    (sum, invoice) => sum + invoice.subtotal,
    0,
  );
  const totalVat = sortedInvoices.reduce(
    (sum, invoice) => sum + invoice.vatTotal,
    0,
  );
  const totalAmount = sortedInvoices.reduce(
    (sum, invoice) => sum + invoice.total,
    0,
  );

  return (
    <div className="bg-white shadow-md rounded p-4">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4">
          {type === "purchase"
            ? "Purchase Invoices"
            : type === "sales"
              ? "Sales Invoices"
              : "Expense Invoices"}
        </Typography>
        <IconButton color="primary" onClick={() => openModal("upload")}>
          <AddIcon />
        </IconButton>
      </Box>
      <TextField
        label="Filter"
        variant="outlined"
        fullWidth
        margin="normal"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      <FilterControls
        yearFilter={yearFilter}
        setYearFilter={setYearFilter}
        monthFilter={monthFilter}
        setMonthFilter={setMonthFilter}
        quarterFilter={quarterFilter}
        setQuarterFilter={setQuarterFilter}
      />
      <InvoiceActions
        selectedInvoices={selectedInvoices}
        onRescan={handleRescan}
        onEdit={() =>
          selectedInvoices.length === 1 &&
          openModal(
            "edit",
            invoices.find((inv) => inv.id === selectedInvoices[0]),
          )
        }
        onDelete={handleDelete}
        onDownload={downloadAllInvoices}
        onExportCSV={exportToCSV}
      />
      <InvoiceTable
        invoices={sortedInvoices}
        selectedInvoices={selectedInvoices}
        onSelectInvoice={handleSelectInvoice}
        onOpenModal={openModal}
        requestSort={requestSort}
        getSortIcon={getSortIcon}
        type={type}
      />
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Typography variant="h6">
          Totals - Subtotal: {totalSubtotal.toFixed(2)}, VAT:{" "}
          {totalVat.toFixed(2)}, Total: {totalAmount.toFixed(2)}
        </Typography>
      </Box>
      <ViewInvoiceModal
        open={modalIsOpen && modalType === "view"}
        onClose={closeModal}
        invoice={selectedInvoice}
        onDownload={downloadInvoice}
      />

      <EditInvoiceModal
        open={modalIsOpen && modalType === "edit"}
        onClose={closeModal}
        invoice={selectedInvoice}
        type={type}
      />

      <Modal
        open={modalIsOpen && modalType === "upload"}
        onClose={closeModal}
        aria-labelledby="upload-invoice-modal-title"
        aria-describedby="upload-invoice-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography
            id="upload-invoice-modal-title"
            variant="h6"
            component="h2"
          >
            {type === "purchase"
              ? "Upload Purchase Invoice"
              : type === "sales"
                ? "Upload Sales Invoice"
                : "Upload Expense Invoice"}
          </Typography>
          <InvoiceUpload type={type} onUploadSuccess={fetchInvoices} />
        </Box>
      </Modal>
    </div>
  );
};

export default Invoices;
